import { INTERVAL } from '@constants';
import { createAsyncThunk } from '@reduxjs/toolkit';
import * as IntegrationService from 'IntegrationModule/services';
import * as Types from 'IntegrationModule/types';
import sortBy from 'lodash/sortBy';
import omit from 'lodash/omit';
import { AppStateType } from 'reducers';
import { integrationListInvalidations } from 'IntegrationModule/api/queries/IntegrationListCrudInvalidations';
import ApiClientManager from 'core/apiClient';

export const fetchDataOverviewPerDataTypes = createAsyncThunk(
  'fetchDataOverviewPerDataTypes',
  async (
    { targetServiceIds, onSuccess, onFailure }: Types.FetchDataOverviewPerDataTypesParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const body = { targetServiceIds };
      const response = await IntegrationService.postDataOverviewPerDataTypes(body, {}, auth_token);

      if (onSuccess) {
        onSuccess({ response });
      }
      return response.data;
    } catch (e) {
      if (onFailure) {
        onFailure({ error: e });
      }
      return rejectWithValue(e);
    }
  },
);

export const fetchDataHistoryPerDataTypes = createAsyncThunk(
  'fetchDataHistoryPerDataTypes',
  async (
    { teamId, interval, limit, offset, isInitialFetch }: Types.fetchDataHistoryPerDataTypesParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    const APIOptions = {
      limit,
      offset,
    };

    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      let payloadData;
      if (isInitialFetch) {
        const responses = await Promise.all(
          Object.values(INTERVAL).map((interval) =>
            IntegrationService.getDataHistoryPerDataTypeByTeamID(
              teamId,
              {
                ...APIOptions,
                interval,
              },
              {},
              auth_token,
            ),
          ),
        );
        const datas = responses.map((response) =>
          sortBy(response.data as Array<Record<string, unknown>>, ['resInterval']),
        );
        payloadData = Object.values(INTERVAL).reduce((acc: any, date: string, index) => {
          const dateObj = {
            [date]: {
              data: [...datas[index]],
              offset: 0,
              hasReachTheEnd: false,
            },
          };
          acc = {
            ...acc,
            ...dateObj,
          };
          return acc;
        }, {});
      } else {
        const response = await IntegrationService.getDataHistoryPerDataTypeByTeamID(
          teamId,
          {
            ...APIOptions,
            interval: interval,
          },
          {},
          auth_token,
        );
        payloadData = {
          [interval]: {
            offset,
            data: [...sortBy(response.data as Array<Record<string, unknown>>, ['resInterval'])],
          },
        };
      }
      return {
        ...payloadData,
        isInitialFetch,
      };
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const fetchPendingEntities = createAsyncThunk(
  'fetchPendingEntities',
  async (
    {
      targetServiceId,
      mappingStatuses,
      dataTypes,
      day,
      onFailure,
      onSuccess,
    }: Types.FetchPendingEntitiesParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const body = {
        targetServiceId,
        mappingStatuses,
        dataTypes,
        startDate: day,
      };
      const response = await IntegrationService.getPendingEntities(body, {}, auth_token);
      if (onSuccess) {
        onSuccess({ response });
      }
      return response.data;
    } catch (e) {
      if (onFailure) {
        onFailure({ error: e });
      }
      return rejectWithValue(e);
    }
  },
);

export const fetchIntegrationServerQueuesStatus = createAsyncThunk(
  'fetchIntegrationServerQueuesStatus',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const response = await IntegrationService.getIntegrationServerQueuesStatus({}, auth_token);
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const fetchApiRequestLog = createAsyncThunk(
  'fetchApiRequestLog',
  async (params: Types.fetchApiRequesLogParams, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();
      const {
        authReducer: { user },
      } = state as AppStateType;
      const auth_token = user?.auth_token;
      const response = await IntegrationService.getApiRequestLog(params, {}, auth_token);
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const fetchNetworkLog = createAsyncThunk(
  'fetchNetworkLog',
  async (params: Types.fetchNetworkLogParams, { dispatch, getState, rejectWithValue }) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const response = await IntegrationService.getNetworkLog(params, {}, auth_token);
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const fetchIntegrationSettingSchema = createAsyncThunk(
  'fetchIntegrationSettingSchema',
  async (
    { targetServiceId, onSuccess, onFailure }: Types.FetchIntegrationsSettingSchemaParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const response = await IntegrationService.fetchIntegrationSettingSchema(
        targetServiceId,
        {},
        auth_token,
      );

      if (onSuccess) {
        onSuccess({ response: response as any });
      }
      return response.data as any;
    } catch (e) {
      if (onFailure) {
        onFailure({ error: e });
      }
      return rejectWithValue(e);
    }
  },
);

export const bulkFetchIntegrationSettingSchema = createAsyncThunk(
  'bulkFetchIntegrationSettingSchema',
  async (
    {
      targetServiceIds,
      meta: { onSuccess, onFailure },
    }: Types.BulkFetchIntegrationsSettingSchemaParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;

      const response = await Promise.all(
        targetServiceIds.map((targetServiceId) =>
          IntegrationService.fetchIntegrationSettingSchema(targetServiceId, {}, auth_token),
        ),
      );

      if (onSuccess) {
        onSuccess({ response: response as any });
      }
      return response as any;
    } catch (e) {
      if (onFailure) {
        onFailure({ error: e });
      }
      return rejectWithValue(e);
    }
  },
);

/* -------------------------------- Error log ------------------------------- */

export const fetchTeamNetworkErrorLog = createAsyncThunk(
  'fetchTeamNetworkErrorLog',
  async (params: Types.fetchTeamNetworkErrorLogParams, { dispatch, getState, rejectWithValue }) => {
    const body = {
      targetService: params.targetService,
      mosaicTeamIds: params.mosaicTeamIds,
      startDate: params.startDate,
      limit: params.limit,
      offset: params.offset,
    };
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const response = await IntegrationService.getNetworkErrorLog(body, {}, auth_token);
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const fetchTeamAgentErrorLog = createAsyncThunk(
  'fetchTeamAgentErrorLog',
  async (
    {
      agentOnly,
      targetService,
      targetServiceId,
      offset,
      limit,
      startDate,
      isInitialFetch,
    }: Types.fetchTeamAgentErrorLogParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    const body = {
      agentOnly: agentOnly || targetService === 'quickbooks-desktop',
      targetService,
      targetServiceId,
      limit,
      offset,
      startDate,
    };
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const response = await IntegrationService.postGeneralRecentErrors(body, {}, auth_token);
      return (response.data as any).response;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const fetchSingleEntity = createAsyncThunk(
  'fetchSingleEntity',
  async (params: Types.FetchSingleEntityParams, { dispatch, getState, rejectWithValue }) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const response = await IntegrationService.getSingleEntity(params, {}, auth_token);
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const fetchIntegrations = createAsyncThunk(
  'fetchIntegrations',
  async (params: Types.FetchIntegrationsParams, { dispatch, getState, rejectWithValue }) => {
    try {
      const { onSuccess, tenant, realmId, ...restParams } = params;
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token ?? '';

      // Build custom headers if tenant/realmId are provided
      const customHeaders =
        tenant && realmId
          ? ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId })
          : undefined;

      const response = await IntegrationService.getIntegrations({
        params: restParams,
        token: auth_token,
        config: customHeaders ? { headers: customHeaders } : {},
      });
      if (onSuccess) {
        onSuccess({ response: response as any });
      }
      return response.data as any;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const fetchCsvImportStatus = createAsyncThunk(
  'fetchCsvImportStatus',
  async (
    { onSuccess, ...params }: Types.FetchCsvImportStatusParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const response = await IntegrationService.getCsvImportStatus(params, params, {}, auth_token);

      if (onSuccess) {
        onSuccess({ response });
      }

      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const updateIntegrationNotes = createAsyncThunk(
  'updateIntegrationNotes',
  async (params: Types.UpdateIntegrationNotesParams, { dispatch, getState, rejectWithValue }) => {
    const { onDone } = params;
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const response = await IntegrationService.updateIntegrationNotes(params, {}, auth_token);
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    } finally {
      if (onDone) {
        onDone();
      }
    }
  },
);

export const fetchDataCountsByEntity = createAsyncThunk(
  'fetchDataCountsByEntity',
  async (
    params: Types.FetchDataCountsByDataTypeParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    const { onSuccess } = params;
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token as string;
      const body = omit(params, ['onSuccess', 'targetServiceId']);
      const response = await IntegrationService.getDataCountsByEntity(params, body, {}, auth_token);

      if (onSuccess) {
        onSuccess({ response });
      }
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const archiveIntegration = createAsyncThunk(
  'archiveIntegration',
  async (params: Types.ArchiveIntegrationParams, { dispatch, getState, rejectWithValue }) => {
    const { onSuccess } = params;
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const response = await IntegrationService.archiveIntegration(
        params,
        {},
        auth_token as string,
      );

      if (onSuccess) {
        onSuccess({ response });
      }

      integrationListInvalidations.onUpdate({
        mosaicTeamId: params.teamId,
      });

      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const unarchiveIntegration = createAsyncThunk(
  'unarchiveIntegration',
  async (params: Types.UnarchiveIntegrationParams, { dispatch, getState, rejectWithValue }) => {
    const { onSuccess } = params;
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const response = await IntegrationService.unarchiveIntegration(
        omit(params, 'onSuccess'),
        {},
        auth_token as string,
      );

      if (onSuccess) {
        onSuccess({ response });
      }
      integrationListInvalidations.onUpdate({
        mosaicTeamId: params.teamId,
      });
      return response.data;
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const fetchIntegrationStoredProcedures = createAsyncThunk(
  'fetchIntegrationStoredProcedures',
  async (
    params: Types.FetchIntegrationStoredProceduresParams,
    { dispatch, getState, rejectWithValue },
  ) => {
    const { onSuccess, onFailure } = params;
    try {
      const {
        authReducer: { user },
      } = getState() as AppStateType;
      const auth_token = user?.auth_token;
      const response = await IntegrationService.fetchIntegrationStoredProcedures(
        params,
        {},
        auth_token,
      );

      if (onSuccess) {
        onSuccess({ response: response as any });
      }
      return response.data as any;
    } catch (e) {
      if (onFailure) {
        onFailure({ error: e });
      }
      return rejectWithValue(e);
    }
  },
);
